import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { Button } from "@/components/primitives/Button";
import { SearchableSelect } from "@/components/primitives/SearchableSelect";
import { Header } from "@/components/layout/Header";
import { PageShell } from "@/components/layout/PageShell";
import { FailureCalendar } from "@/components/features/dashboard/FailureCalendar";
import { KpiCards } from "@/components/features/dashboard/KpiCards";
import { RecentFailuresTable } from "@/components/features/dashboard/RecentFailuresTable";
import { RunHistory } from "@/components/features/dashboard/RunHistory";
import { StatusCodeChart } from "@/components/features/dashboard/StatusCodeChart";
import { SuccessRateChart } from "@/components/features/dashboard/SuccessRateChart";
import { TrendChart } from "@/components/features/dashboard/TrendChart";
import { useFlows } from "@/hooks/useFlows";
import { useFlowStats } from "@/hooks/useFlowStats";

const PERIOD_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

export const DashboardPage: React.FC = () => {
  const { flowId } = useParams<{ flowId?: string }>();
  const { flows, isLoading: flowsLoading, refresh: refreshFlows } = useFlows();
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(flowId || null);
  const [days, setDays] = useState<number>(30);
  const [selectedStepId, setSelectedStepId] = useState<string>("");
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    if (flowId) {
      setSelectedFlowId(flowId);
    }
  }, [flowId]);

  const effectiveFlowId = selectedFlowId ?? (flows[0]?.id || null);
  const effectiveFlow = flows.find((f) => f.id === effectiveFlowId);
  const steps = effectiveFlow?.steps ?? [];
  const isSoftnu = effectiveFlow?.flow_type === "softnu_checks";
  const activeStepId = isSoftnu && steps.some((step) => step.id === selectedStepId) ? selectedStepId : "";
  const selectedStep = steps.find((step) => step.id === activeStepId);
  const { stats, isLoading: statsLoading, error, refresh } = useFlowStats(effectiveFlowId, days, activeStepId);

  return (
    <PageShell>
      <Header
        action={
          <Button variant="secondary" onClick={() => (window.location.href = "/")}>
            Back to status
          </Button>
        }
      />

      <main className="page-container py-10">
        <div className="mb-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Service Dashboard
          </h2>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            {isSoftnu ? "Backend uptime and execution history for each discovered service." : "Health trends, uptime, and incident history for each service."}
          </p>
        </div>

        <div className="mb-8 flex flex-wrap items-end gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="flow-select"
              className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Service / Flow
            </label>
            <select
              id="flow-select"
              value={effectiveFlowId ?? ""}
              onChange={(e) => setSelectedFlowId(e.target.value)}
              disabled={flowsLoading}
              className="min-w-[220px] rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-softnu-500 focus:outline-none focus:ring-2 focus:ring-softnu-500/20 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {flows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.name}
                </option>
              ))}
            </select>
          </div>

          {isSoftnu && (
            <div className="flex max-w-full flex-col gap-2">
              <label htmlFor="service-select" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">View</label>
              <SearchableSelect
                key={effectiveFlowId}
                id="service-select"
                value={activeStepId}
                onChange={setSelectedStepId}
                placeholder="Search by service name or type…"
                options={[
                  { value: "", label: "Backend health" },
                  ...steps.filter((step) => step.step_type === "imported_event").map((step) => ({ value: step.id, label: `${step.name} · ${step.event_type}` })),
                ]}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Period
            </span>
            <div className="flex gap-2">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDays(option.value)}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
                    days === option.value
                      ? "border-softnu-600 bg-softnu-600 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ml-auto">
            <Button onClick={() => { refreshFlows(); refresh(); setHistoryRefreshKey((key) => key + 1); }} disabled={statsLoading || !effectiveFlowId}>
              Refresh
            </Button>
          </div>
        </div>

        {statsLoading && (
          <div className="py-20 text-center text-slate-500 dark:text-slate-400">
            Loading dashboard…
          </div>
        )}
        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {!statsLoading && stats && (
          <>
            {isSoftnu && (
              <p className="mb-5 text-sm text-slate-600 dark:text-slate-300">
                Uptime comes from the separately scheduled health URL. {effectiveFlow?.source_config?.logs_url ? `Logs last polled: ${effectiveFlow.last_log_poll_at ? new Date(effectiveFlow.last_log_poll_at).toLocaleString() : "not yet"}.` : "Add a logs URL in the flow editor to discover services."}
                {effectiveFlow?.last_log_poll_error && <span className="block text-red-600 dark:text-red-400">Logs polling error: {effectiveFlow.last_log_poll_error}</span>}
                {effectiveFlow?.source_config?.logs_url && <span className="block">Next index: {effectiveFlow.log_poll_state?.next_index ?? effectiveFlow.source_config.start_index} · Last imported: {effectiveFlow.log_poll_state?.last_imported_index ?? "—"} · Source cleanup: {effectiveFlow.source_config.delete_after_import ? `enabled · last deleted index ${effectiveFlow.log_poll_state?.last_deleted_index ?? "—"}` : "disabled (read-only)"} · Pending ranges: {effectiveFlow.log_poll_state?.pending_deletes?.length ?? 0}</span>}
              </p>
            )}
            {selectedStep && <p className="mb-5 [overflow-wrap:anywhere] text-sm text-slate-600 dark:text-slate-300">Showing imported {selectedStep.event_type} events for {selectedStep.name}. Event success rate is based on classified results; no events means unknown, not down.</p>}
            <KpiCards stats={stats} />

            <div className="mb-6">
              <FailureCalendar dailyBreakdown={stats.daily_breakdown} />
            </div>

            <div className="mb-6 grid gap-6 lg:grid-cols-2">
              <TrendChart dailyBreakdown={stats.daily_breakdown} />
              <SuccessRateChart success={stats.success_count} failure={stats.failure_count} />
            </div>

            <div className="mb-6 grid gap-6 lg:grid-cols-2">
              {(!selectedStep || selectedStep.event_type === "HttpRequest") ? <StatusCodeChart distribution={stats.status_code_distribution} /> : <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900"><h3 className="text-lg font-semibold text-slate-900 dark:text-white">{selectedStep.event_type === "Cron" ? "Cron executions" : "Event type"}</h3><p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{selectedStep.event_type === "Cron" ? "Completion and failure results are tracked per job. Open an event below to inspect its schedule, execution source, start/finish times, result, and full metadata." : "Full metadata is available in each event. A type-specific chart can be added once its schema is defined."}</p></div>}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-1 text-lg font-semibold text-slate-900 dark:text-white">
                  Checks Overview
                </h3>
                <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                  Total runs during the selected period.
                </p>
                <div className="flex min-h-[240px] items-center justify-center">
                  <span className="bg-gradient-to-r from-softnu-600 to-emerald-500 bg-clip-text text-5xl font-bold text-transparent">
                    {stats.total_runs}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <RecentFailuresTable failures={stats.recent_failures} steps={steps} />
            </div>

            {effectiveFlowId && <RunHistory key={`${effectiveFlowId}:${activeStepId}`} flowId={effectiveFlowId} steps={steps} refreshKey={historyRefreshKey} stepId={activeStepId || (isSoftnu ? steps.find((step) => step.is_managed && step.step_type === "http")?.id : undefined)} />}
          </>
        )}
      </main>
    </PageShell>
  );
};
